/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the application
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  browserPopupRedirectResolver,
  UserCredential,
  sendPasswordResetEmail,
  sendEmailVerification,
  applyActionCode,
  multiFactor,
  TotpMultiFactorGenerator,
  TotpSecret,
  MultiFactorError,
  getMultiFactorResolver,
  MultiFactorResolver,
  PhoneMultiFactorGenerator,
  PhoneAuthProvider,
} from 'firebase/auth';
import { doc, getDoc, increment, serverTimestamp, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../lib/firebase';
import { shouldResolveAuthRedirect } from '../lib/authRedirectResult';
import { isPlatformAdmin as checkPlatformAdmin } from '../lib/platformAdmin';
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  NotificationPreferences,
  normalizeNotificationPreferences,
} from '../types/notificationPreferences';

/**
 * User Profile from Firestore
 */
export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  companyName: string;
  role: 'admin' | 'analyst' | 'viewer';
  team: string[];
  organizationId: string;
  permissions: {
    canManageUsers: boolean;
    dealIds: string[];
    canCreateDeals: boolean;
    canEditDeals: boolean;
    canDeleteDeals: boolean;
    canViewAnalytics: boolean;
  };
  preferences: Record<string, any>;
  notificationPreferences: NotificationPreferences;
  photoURL?: string;
  avatarUrl?: string;
  loginCount?: number;
  lastAvatarPromptLoginCount?: number;
  lastLoginAt?: Date;
  status: 'active' | 'inactive' | 'invited' | 'suspended';
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Auth Context Value
 */
interface AuthContextValue {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isPlatformAdmin: boolean;
  signUp: (email: string, password: string, displayName: string) => Promise<UserCredential>;
  signIn: (email: string, password: string) => Promise<UserCredential>;
  signInWithGoogle: () => Promise<UserCredential>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  sendVerificationEmail: (user: any) => Promise<void>;
  verifyEmailWithCode: (oobCode: string) => Promise<void>;
  reloadUser: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  enrollMFA: () => Promise<string>;
  verifyMFA: (verificationCode: string) => Promise<void>;
  unenrollMFA: () => Promise<void>;
  mfaRequired: boolean;
  mfaResolver: MultiFactorResolver | null;
  resolveMFA: (verificationCode: string) => Promise<UserCredential>;
  isEmailVerified: boolean;
  isMfaEnrolled: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Hook to access auth context
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Get organization ID based on email domain
 * @param email User's email address
 * @returns Organization ID for the user's domain
 */
/**
 * Guest emails from public domains granted corbelcap.com domain access.
 * Maps specific email addresses to their effective domain for org assignment.
 */
const EMAIL_TO_DOMAIN_OVERRIDE: Record<string, string> = {
  'adel.lotfi.benslimane@gmail.com': 'corbelcap.com',
};

const getOrganizationIdFromEmail = (email: string): string => {
  const normalizedEmail = email.toLowerCase();
  const domain = normalizedEmail.split('@')[1];
  if (!domain) return 'default-org';

  // Check guest email overrides first (personal emails → corbelcap.com domain)
  const effectiveDomain = EMAIL_TO_DOMAIN_OVERRIDE[normalizedEmail] || domain;

  // Known domain mappings
  // NOTE: We own returncatalyst.ai, NOT returncatalyst.com
  const domainToOrg: Record<string, string> = {
    'corbelcap.com': 'corbel-capital',
    'corbelcap.ai': 'corbel-capital',
    'returncatalyst.ai': 'returncatalyst',
  };

  if (domainToOrg[effectiveDomain]) {
    return domainToOrg[effectiveDomain];
  }

  // For any other approved domain, derive org ID from domain name
  // e.g., "centuryparkcapital.com" → "centuryparkcapital"
  const orgSlug = effectiveDomain.split('.')[0];
  return orgSlug || 'default-org';
};

type UserRole = UserProfile['role'];
type UserPermissions = UserProfile['permissions'];

const isUserRole = (value: unknown): value is UserRole => (
  value === 'admin' || value === 'analyst' || value === 'viewer'
);

const getDefaultPermissionsForRole = (role: UserRole): UserPermissions => {
  if (role === 'admin') {
    return {
      canManageUsers: true,
      dealIds: [],
      canCreateDeals: true,
      canEditDeals: true,
      canDeleteDeals: true,
      canViewAnalytics: true,
    };
  }

  if (role === 'viewer') {
    return {
      canManageUsers: false,
      dealIds: [],
      canCreateDeals: false,
      canEditDeals: false,
      canDeleteDeals: false,
      canViewAnalytics: false,
    };
  }

  return {
    canManageUsers: false,
    dealIds: [],
    canCreateDeals: true,
    canEditDeals: true,
    canDeleteDeals: false,
    canViewAnalytics: true,
  };
};

const getAuthProfileDefaults = async (
  user: User,
  emailForFallback: string,
): Promise<{
  organizationId: string;
  role: UserRole;
  permissions: UserPermissions;
}> => {
  try {
    const tokenResult = await user.getIdTokenResult(false);
    const claimOrg = tokenResult.claims.organizationId;
    const claimRole = tokenResult.claims.role;
    const organizationId = typeof claimOrg === 'string' && claimOrg.length > 0
      ? claimOrg
      : getOrganizationIdFromEmail(emailForFallback);
    const role: UserRole = isUserRole(claimRole) ? claimRole : 'analyst';

    return {
      organizationId,
      role,
      permissions: getDefaultPermissionsForRole(role),
    };
  } catch (err) {
    console.warn('[AuthProvider] getIdTokenResult failed, falling back to email/default role', err);
    return {
      organizationId: getOrganizationIdFromEmail(emailForFallback),
      role: 'analyst',
      permissions: getDefaultPermissionsForRole('analyst'),
    };
  }
};

const toDate = (value: any): Date | undefined => {
  if (!value) return undefined;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  if (typeof value === 'number' || typeof value === 'string') return new Date(value);
  if (value.seconds != null) return new Date(value.seconds * 1000);
  return undefined;
};

/**
 * Auth Provider Component
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaResolver, setMfaResolver] = useState<MultiFactorResolver | null>(null);
  const [enrollmentSecret, setEnrollmentSecret] = useState<TotpSecret | null>(null);

  // DEBUG: Track auth initialization
  console.log('[AuthProvider] Component rendering, loading:', loading, 'user:', user?.email || 'null');

  /**
   * Load user profile from Firestore
   */
  const loadUserProfile = async (uid: string) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const email = data.email || '';
        const authDefaults = auth.currentUser
          ? await getAuthProfileDefaults(auth.currentUser, email)
          : null;
        const effectiveRole = isUserRole(data.role)
          ? data.role
          : authDefaults?.role || 'analyst';
        const effectivePermissions = data.permissions || getDefaultPermissionsForRole(effectiveRole);

        // Audit 2026-04-21 Blocker #6 Phase 1: the client no longer
        // rewrites `organizationId` on the user doc. The Firestore rules
        // now reject self-update of auth fields, so the previous rewrite
        // would fail anyway. Authoritative tenant identity lives in the
        // Firebase Auth custom claim populated by the beforeCreate
        // blocking function (Phase 2 backfills existing users).

        // Check if user is suspended
        if (data.status === 'suspended') {
          console.warn('[AuthProvider] User account is suspended, signing out:', email);
          await signOut(auth);
          setUser(null);
          setUserProfile(null);
          throw new Error('Your account has been suspended. Contact your administrator.');
        }

        setUserProfile({
          uid,
          email: data.email,
          displayName: data.displayName,
          companyName: data.companyName || '',
          role: effectiveRole,
          team: data.team || [],
          organizationId: data.organizationId || authDefaults?.organizationId || 'default-org',
          permissions: effectivePermissions,
          preferences: data.preferences || {},
          notificationPreferences: normalizeNotificationPreferences(
            data.preferences?.notifications || data.notificationPreferences
          ),
          photoURL: data.photoURL || data.avatarUrl || undefined,
          avatarUrl: data.avatarUrl || data.photoURL || undefined,
          loginCount: typeof data.loginCount === 'number' ? data.loginCount : 0,
          lastAvatarPromptLoginCount: typeof data.lastAvatarPromptLoginCount === 'number'
            ? data.lastAvatarPromptLoginCount
            : 0,
          lastLoginAt: toDate(data.lastLoginAt),
          status: data.status || 'active',
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        });
      } else {
        // Create default profile if it doesn't exist
        const currentAuthUser = auth.currentUser || user;
        const email = currentAuthUser?.email || '';
        const emailDomain = email.split('@')[1]?.toLowerCase() || '';
        // Audit 2026-04-21 Blocker #6 Phase 1: prefer the claim so the new
        // profile aligns with the server's authoritative tenant identity.
        const authDefaults = currentAuthUser
          ? await getAuthProfileDefaults(currentAuthUser, email)
          : {
            organizationId: getOrganizationIdFromEmail(email),
            role: 'analyst' as const,
            permissions: getDefaultPermissionsForRole('analyst'),
          };

        const defaultProfile: UserProfile = {
          uid,
          email,
          displayName: currentAuthUser?.displayName || currentAuthUser?.email?.split('@')[0] || 'User',
          companyName: '',
          role: authDefaults.role,
          team: [],
          organizationId: authDefaults.organizationId,
          permissions: authDefaults.permissions,
          preferences: {
            notifications: DEFAULT_NOTIFICATION_PREFERENCES,
          },
          notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES,
          loginCount: 0,
          lastAvatarPromptLoginCount: 0,
          status: 'active',
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        if (currentAuthUser?.photoURL) {
          defaultProfile.photoURL = currentAuthUser.photoURL;
          defaultProfile.avatarUrl = currentAuthUser.photoURL;
        }
        await setDoc(doc(db, 'users', uid), {
          ...defaultProfile,
          emailDomain,
        });
        setUserProfile(defaultProfile);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  /**
   * Sign up with email and password
   */
  const signUp = async (email: string, password: string, displayName: string) => {
    // Validate email domain before creating account
    const validateEmail = httpsCallable<{ email: string }, { allowed: boolean; reason?: string }>(
      functions, 'validateEmailForRegistration'
    );
    const validation = await validateEmail({ email });
    if (!validation.data.allowed) {
      throw new Error(validation.data.reason || 'Registration not allowed for this email');
    }

    const credential = await createUserWithEmailAndPassword(auth, email, password);

    // Prefer authoritative custom claims so the first user in a new org is
    // written as admin when the blocking function bootstraps that role.
    const authDefaults = await getAuthProfileDefaults(credential.user, email);
    const emailDomain = email.split('@')[1]?.toLowerCase() || '';

    // Create user profile in Firestore
    const profile: UserProfile = {
      uid: credential.user.uid,
      email,
      displayName,
      companyName: '',
      role: authDefaults.role,
      team: [],
      organizationId: authDefaults.organizationId,
      permissions: authDefaults.permissions,
      preferences: {
        notifications: DEFAULT_NOTIFICATION_PREFERENCES,
      },
      notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES,
      loginCount: 1,
      lastAvatarPromptLoginCount: 0,
      lastLoginAt: new Date(),
      status: 'active',
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    if (credential.user.photoURL) {
      profile.photoURL = credential.user.photoURL;
      profile.avatarUrl = credential.user.photoURL;
    }

    await setDoc(doc(db, 'users', credential.user.uid), {
      ...profile,
      emailDomain,
    });

    return credential;
  };

  const recordSuccessfulLogin = async (firebaseUser: User) => {
    await setDoc(doc(db, 'users', firebaseUser.uid), {
      loginCount: increment(1),
      lastLoginAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  };

  const recordSuccessfulLoginAndRefreshProfile = async (firebaseUser: User) => {
    await loadUserProfile(firebaseUser.uid);
    await recordSuccessfulLogin(firebaseUser);
    await loadUserProfile(firebaseUser.uid);
  };

  /**
   * Sign in with email and password
   */
  const signIn = async (email: string, password: string) => {
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      await recordSuccessfulLoginAndRefreshProfile(credential.user);
      return credential;
    } catch (error: any) {
      if (error.code === 'auth/multi-factor-auth-required') {
        setMfaResolver(getMultiFactorResolver(auth, error));
        setMfaRequired(true);
        throw error;
      }
      throw error;
    }
  };

  /**
   * Sign in with Google — popup-first with redirect fallback
   *
   * FEB 2026: Restored popup-first approach (last known working state: commit 7c928a95).
   * The redirect flow broke due to cross-origin authDomain (web.app vs firebaseapp.com)
   * causing getRedirectResult to return null in Chrome 115+.
   *
   * Popup flow works regardless of authDomain cross-origin issues.
   * Falls back to redirect only if popup is blocked by browser.
   * COOP console warnings are cosmetic and don't block popup auth.
   */
  const signInWithGoogle = async (): Promise<UserCredential> => {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });

    try {
      const result = await signInWithPopup(auth, provider, browserPopupRedirectResolver);
      await recordSuccessfulLoginAndRefreshProfile(result.user);
      return result;
    } catch (error: any) {
      // Fallback to redirect if popup is blocked
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user') {
        console.log('[AuthProvider] Popup blocked/closed, falling back to redirect...');
        return signInWithRedirect(
          auth,
          provider,
          browserPopupRedirectResolver
        ) as unknown as Promise<UserCredential>;
      }
      if (error.code === 'auth/multi-factor-auth-required') {
        setMfaResolver(getMultiFactorResolver(auth, error));
        setMfaRequired(true);
        throw error;
      }
      throw error;
    }
  };

  /**
   * Sign out
   */
  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setUserProfile(null);
    // Clear Sentry user context on logout
    import('@sentry/react').then(Sentry => Sentry.setUser(null)).catch(() => {});
  };

  /**
   * Send password reset email
   *
   * Includes actionCodeSettings so the reset link directs to our custom
   * /auth/action handler instead of Firebase's default page.
   */
  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email, {
      url: `${window.location.origin}/auth/action`,
      handleCodeInApp: true,
    });
  };

  /**
   * Send verification email with continueUrl so user can return to app
   */
  const sendVerificationEmail = async (user: any) => {
    await sendEmailVerification(user, {
      url: `${window.location.origin}/verify-email`,
      handleCodeInApp: false,
    });
  };

  /**
   * Verify email using an out-of-band action code (from verification link)
   */
  const verifyEmailWithCode = async (oobCode: string) => {
    await applyActionCode(auth, oobCode);
    if (auth.currentUser) {
      await auth.currentUser.reload();
      setUser({ ...auth.currentUser } as User);
    }
  };

  /**
   * Enroll in MFA (TOTP)
   */
  const enrollMFA = async (): Promise<string> => {
    if (!auth.currentUser) throw new Error('No user logged in');

    const session = await multiFactor(auth.currentUser).getSession();
    const secret = await TotpMultiFactorGenerator.generateSecret(session);

    setEnrollmentSecret(secret);

    // Return the secret URI for QR code generation
    return secret.generateQrCodeUrl(
      auth.currentUser.email || 'Corbel Capital',
      'Corbel Capital AI'
    );
  };

  /**
   * Verify and finalize MFA enrollment
   */
  const verifyMFA = async (verificationCode: string) => {
    if (!auth.currentUser) throw new Error('No user logged in');
    if (!enrollmentSecret) throw new Error('No enrollment session active');

    const multiFactorUser = multiFactor(auth.currentUser);
    const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
      enrollmentSecret,
      verificationCode
    );

    await multiFactorUser.enroll(assertion, 'Authenticator App');
    setEnrollmentSecret(null);
  };

  /**
   * Unenroll MFA
   */
  const unenrollMFA = async () => {
    if (!auth.currentUser) throw new Error('No user logged in');
    const enrolledFactors = multiFactor(auth.currentUser).enrolledFactors;
    // Unenroll all factors for now
    for (const factor of enrolledFactors) {
      await multiFactor(auth.currentUser).unenroll(factor);
    }
  };

  /**
   * Resolve MFA Challenge during Sign In
   */
  const resolveMFA = async (verificationCode: string) => {
    if (!mfaResolver) throw new Error('No MFA resolver available');

    const hints = mfaResolver.hints;
    const sessionId = mfaResolver.session;

    // Assuming TOTP is the first/only hint for now
    // In a real app, we might check hints[0].factorId
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(
      hints[0].uid,
      verificationCode
    );

    const credential = await mfaResolver.resolveSignIn(assertion);
    await recordSuccessfulLoginAndRefreshProfile(credential.user);
    setMfaRequired(false);
    setMfaResolver(null);
    return credential;
  };

  /**
   * Reload user to check verification status
   */
  const reloadUser = async () => {
    if (auth.currentUser) {
      await auth.currentUser.reload();
      setUser({ ...auth.currentUser });
    }
  };

  /**
   * Refresh user profile from Firestore (e.g. after settings change)
   */
  const refreshProfile = async () => {
    if (auth.currentUser) {
      await loadUserProfile(auth.currentUser.uid);
    }
  };

  /**
   * Handle Google redirect results only on the routes that can start the
   * popup-to-redirect fallback. Firebase checks its own session-scoped pending
   * marker before initializing the explicit resolver, so fresh auth pages stay
   * iframe-free while legitimate redirect returns are still completed.
   */
  useEffect(() => {
    if (!shouldResolveAuthRedirect(window.location.pathname)) return;

    console.log('[AuthProvider] handleRedirectResult useEffect starting');
    const handleRedirectResult = async () => {
      console.log('[AuthProvider] handleRedirectResult() called, checking for redirect result...');
      try {
        const result = await getRedirectResult(auth, browserPopupRedirectResolver);
        console.log('[AuthProvider] getRedirectResult completed:', result ? 'Has result' : 'No result');
        if (result && result.user) {
          // User successfully signed in via redirect
          const email = result.user.email || '';
          const emailDomain = email.split('@')[1]?.toLowerCase() || '';
          // Audit 2026-04-21 Blocker #6 Phase 1: prefer custom claims.
          const authDefaults = await getAuthProfileDefaults(result.user, email);

          const userDoc = await getDoc(doc(db, 'users', result.user.uid));
          if (!userDoc.exists()) {
            const profile: UserProfile = {
              uid: result.user.uid,
              email,
              displayName: result.user.displayName || result.user.email?.split('@')[0] || 'User',
              companyName: '',
              role: authDefaults.role,
              team: [],
              organizationId: authDefaults.organizationId,
              permissions: authDefaults.permissions,
              preferences: {
                notifications: DEFAULT_NOTIFICATION_PREFERENCES,
              },
              notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES,
              loginCount: 0,
              lastAvatarPromptLoginCount: 0,
              lastLoginAt: new Date(),
              status: 'active',
              createdAt: new Date(),
              updatedAt: new Date(),
            };
            if (result.user.photoURL) {
              profile.photoURL = result.user.photoURL;
              profile.avatarUrl = result.user.photoURL;
            }
            await setDoc(doc(db, 'users', result.user.uid), {
              ...profile,
              emailDomain,
            });
          } else {
            const userData = userDoc.data();
            if (userData.organizationId !== authDefaults.organizationId && authDefaults.organizationId !== 'default-org') {
              console.warn('[AuthProvider] Existing profile organization differs from auth claim; backend migration required', {
                uid: result.user.uid,
                profileOrganizationId: userData.organizationId,
                claimOrganizationId: authDefaults.organizationId,
              });
            }
          }
          await recordSuccessfulLoginAndRefreshProfile(result.user);
        }
      } catch (error: any) {
        // Ignore "missing initial state" errors - these happen on fresh page loads
        if (!error.message?.includes('missing initial state')) {
          console.error('[AuthProvider] Google redirect error:', error);
        } else {
          console.log('[AuthProvider] getRedirectResult: No pending redirect (fresh page load)');
        }
      }
      console.log('[AuthProvider] handleRedirectResult() completed');
    };

    handleRedirectResult();
  }, []);

  /**
   * Listen for auth state changes
   *
   * Safety timeout: If onAuthStateChanged never fires (e.g., Firebase SDK
   * internal hang, IndexedDB lock, network issues), force loading to false
   * after 5 seconds so the login page renders instead of an infinite spinner.
   */
  useEffect(() => {
    console.log('[AuthProvider] onAuthStateChanged useEffect starting, setting up listener...');
    let authResolved = false;

    const authTimeout = setTimeout(() => {
      if (!authResolved) {
        console.warn('[AuthProvider] Auth state timeout after 5s - onAuthStateChanged never fired. Forcing loading=false.');
        setLoading(false);
      }
    }, 5000);

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      authResolved = true;
      clearTimeout(authTimeout);
      console.log('[AuthProvider] onAuthStateChanged FIRED! User:', firebaseUser?.email || 'null', 'UID:', firebaseUser?.uid || 'none');
      try {
        setUser(firebaseUser);
        if (firebaseUser) {
          // Set Sentry user context for error attribution
          import('@sentry/react').then(Sentry => Sentry.setUser({ id: firebaseUser.uid })).catch(() => {});
          console.log('[AuthProvider] User authenticated, loading profile for:', firebaseUser.uid);
          await loadUserProfile(firebaseUser.uid);
          console.log('[AuthProvider] Profile loaded successfully');
        } else {
          console.log('[AuthProvider] No user, clearing profile');
          setUserProfile(null);
        }
      } catch (error) {
        console.error('[AuthProvider] Error in auth state change handler:', error);
        // Still set the user even if profile loading fails
        setUserProfile(null);
      } finally {
        // CRITICAL: Always set loading to false, even if loadUserProfile throws
        console.log('[AuthProvider] Setting loading to FALSE');
        setLoading(false);
      }
    });

    console.log('[AuthProvider] onAuthStateChanged listener registered');
    return () => {
      clearTimeout(authTimeout);
      console.log('[AuthProvider] onAuthStateChanged listener cleanup');
      unsubscribe();
    };
  }, []);

  const value: AuthContextValue = {
    user,
    userProfile,
    loading,
    isPlatformAdmin: checkPlatformAdmin(user?.email),
    signUp,
    signIn,
    signInWithGoogle,
    logout,
    resetPassword,
    sendVerificationEmail,
    verifyEmailWithCode,
    reloadUser,
    refreshProfile,
    enrollMFA,
    verifyMFA,
    unenrollMFA,
    mfaRequired,
    mfaResolver,
    resolveMFA,
    isEmailVerified: user ? user.emailVerified : false,
    isMfaEnrolled: user ? multiFactor(user).enrolledFactors.length > 0 : false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
